import { GatewayQuestionnaire } from "@conformite/gateway";
import { UseFormWatch } from "@endya-conformite/ui-kit/src/lya/react-hook-form";
import { useSharedAssujettiAnswers } from "@src/components/questionnaire/SharedAssujettiAnswersProvider";
import { getSharedAssujettiAnswerKey } from "@src/helper/sharedAssujettiQuestions.helper";
import { MutableRefObject, useCallback, useEffect, useMemo } from "react";

type QuestionDef = GatewayQuestionnaire.QuestionDefinitionResponse;

type UseSharedAnswerSyncParams = {
  slug: GatewayQuestionnaire.QuestionnaireSlug | undefined;
  questions: QuestionDef[];
  isReady: boolean;
  watch: UseFormWatch<Record<string, string>>;
  getValues: (name: string) => unknown;
  setValue: (
    name: string,
    value: string,
    options?: { shouldDirty?: boolean }
  ) => void;
  deselectedSharedQsRef: MutableRefObject<Set<string>>;
};

export function useSharedAnswerSync({
  slug,
  questions,
  isReady,
  watch,
  getValues,
  setValue,
  deselectedSharedQsRef,
}: UseSharedAnswerSyncParams) {
  const context = useSharedAssujettiAnswers();
  const sharedAnswers = context?.sharedAnswers;
  const publishSharedAnswer = context?.publishSharedAnswer;

  const sharedQuestions = useMemo(() => {
    if (!context) return [];
    return questions.flatMap((question) => {
      const key = getSharedAssujettiAnswerKey(slug, question.order);
      return key ? [{ question, key }] : [];
    });
  }, [context, questions, slug]);

  const sharedByQuestionId = useMemo(() => {
    const map = new Map<string, { key: string; question: QuestionDef }>();
    sharedQuestions.forEach(({ question, key }) =>
      map.set(question.id, { key, question })
    );
    return map;
  }, [sharedQuestions]);

  // PUBLICATION : dès que le vérificateur modifie une question partagée, on
  // pousse la nouvelle réponse dans le contexte (overwrite) pour que tous les
  // autres axes / personnes se mettent à jour immédiatement. On ne réagit
  // qu'aux changements utilisateur (`type === "change"`) : les mises à jour
  // programmatiques (reset, setValue de la consommation ci-dessous) portent un
  // `type` indéfini et ne redéclenchent donc pas de publication (anti-boucle).
  useEffect(() => {
    if (!publishSharedAnswer || !isReady) return undefined;
    const subscription = watch((values, { name, type }) => {
      if (type !== "change" || !name) return;
      const entry = sharedByQuestionId.get(name);
      if (!entry) return;
      const value = values[name];
      if (!value) return;
      const label = entry.question.options.find((o) => o.id === value)?.label;
      if (label) publishSharedAnswer(entry.key, label, true);
    });
    return () => subscription.unsubscribe();
  }, [publishSharedAnswer, isReady, watch, sharedByQuestionId]);

  useEffect(() => {
    if (!isReady || !sharedAnswers) return;
    sharedQuestions.forEach(({ question, key }) => {
      if (deselectedSharedQsRef.current.has(question.id)) return;
      const label = sharedAnswers[key];
      if (!label) return;
      const optionId = question.options.find((o) => o.label === label)?.id;
      if (!optionId) return;
      if (getValues(question.id) === optionId) return;
      setValue(question.id, optionId, { shouldDirty: true });
    });
  }, [isReady, sharedAnswers, sharedQuestions, getValues, setValue, deselectedSharedQsRef]);

  const publishSharedFromSave = useCallback(
    (savedAnswers: Record<string, string>) => {
      if (!publishSharedAnswer) return;
      sharedQuestions.forEach(({ question, key }) => {
        const value = savedAnswers[question.id];
        if (!value) return;
        const label = question.options.find((o) => o.id === value)?.label;
        if (label) publishSharedAnswer(key, label, true);
      });
    },
    [publishSharedAnswer, sharedQuestions]
  );

  return { publishSharedFromSave };
}
